// @file Base62 encoding and decoding utility
// Uses alphanumeric characters (0-9, A-Z, a-z) for encoding
// Compatible with the backend Ruby Base62 implementation at app/lib/base62.rb

const BASE62_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
const BASE62_BASE = BASE62_CHARS.length

/**
 * Encode a string to a Base62 string
 * @param input The string to encode
 * @returns Base62 encoded string
 */
export function encodeString(input: string): string {
  if (input === '') return encodeBigInt(BigInt(0))

  // Convert string to bytes, then to a big integer
  const bytes = new TextEncoder().encode(input)
  let number = BigInt(0)

  // Build a single large number from all bytes by treating them as a big-endian integer
  // The bit shifting operation implements the polynomial: byte[0]*256^n + byte[1]*256^(n-1) + ... + byte[n]*256^0
  // Example: 'hi' → [104, 105] → 104*256^1 + 105*256^0 = 104*256 + 105*1 = 26,729
  for (const byte of bytes) {
    number = (number << BigInt(8)) + BigInt(byte)
  }

  return encodeBigInt(number)
}

/**
 * Decode a Base62 string back to the original string
 * @param encoded The Base62 string to decode
 * @returns Original decoded string
 */
export function decodeString(encoded: string): string {
  if (!encoded) {
    throw new Error('Input cannot be null or empty')
  }

  // Decode to number first
  const number = decodeToBigInt(encoded)
  if (number === BigInt(0)) return ''

  // Convert number back to bytes, then to string
  const bytes: number[] = []
  let remaining = number

  while (remaining > BigInt(0)) {
    bytes.unshift(Number(remaining & BigInt(0xff)))
    remaining = remaining >> BigInt(8)
  }

  return new TextDecoder().decode(new Uint8Array(bytes))
}

/**
 * Internal function to encode a BigInt to Base62
 * @param number The BigInt to encode
 * @returns Base62 encoded string
 */
function encodeBigInt(number: bigint): string {
  if (number === BigInt(0)) return '0'

  let result = ''
  while (number > BigInt(0)) {
    result = BASE62_CHARS[Number(number % BigInt(BASE62_BASE))] + result
    number = number / BigInt(BASE62_BASE)
  }
  return result
}

/**
 * Internal function to decode Base62 string to BigInt
 * @param encoded The Base62 string to decode
 * @returns Decoded BigInt
 */
function decodeToBigInt(encoded: string): bigint {
  let result = BigInt(0)

  for (const char of encoded) {
    const index = BASE62_CHARS.indexOf(char)
    if (index === -1) {
      throw new Error(`Invalid character '${char}' in Base62 string`)
    }
    result = result * BigInt(BASE62_BASE) + BigInt(index)
  }

  return result
}
